import { useState, useEffect } from 'react';

export default function Hero() {
  const [scrolled, setScrolled] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Trigger animations after a brief delay
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const scrollToWaitlist = () => {
    const waitlistSection = document.getElementById('waitlist');
    if (waitlistSection) {
      waitlistSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-r from-[#1a2332] via-[#1f2937] to-[#2d3748] overflow-hidden">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://static.readdy.ai/image/57253257ce2fc83925052891bf004756/264fa5438bc5a5dc88c73fbd09ddf478.jpeg" 
              alt="Atlas Contractor Solutions Logo" 
              className="h-10 w-10 object-contain"
            />
            <span className={`font-semibold text-lg tracking-tight ${scrolled ? 'text-gray-900' : 'text-white'}`}>
              Atlas Contractor Solutions
            </span>
          </div>
          <button 
            onClick={scrollToWaitlist}
            className={`px-6 py-2.5 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${scrolled ? 'bg-[#ff6b35] text-white hover:bg-[#ff5722]' : 'bg-white/10 text-white hover:bg-white/20'}`}
          >
            Join Waitlist
          </button>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20 min-h-screen flex items-center">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center w-full">
          {/* Left Content */}
          <div className={`space-y-8 transition-all duration-1000 delay-500 ${loaded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight">
              Stop drowning in paperwork. Start building.
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              AI-powered business platform built for contractors who work with their hands, not spreadsheets
            </p>
            <div className="space-y-4">
              <button 
                onClick={scrollToWaitlist}
                className="px-8 py-4 bg-[#ff6b35] text-white rounded-full font-semibold text-lg hover:bg-[#ff5722] transition-all hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
              >
                Join the Waitlist
                <i className="ri-arrow-right-line"></i>
              </button>
              <p className="text-sm text-gray-400">
                Built for 40+ trades • No credit card required
              </p>
            </div>
          </div>

          {/* Right Visual - AtlasIQ Brand Name */}
          <div className="relative h-96 lg:h-[500px] flex items-center justify-center">
            <div className="relative flex flex-col items-center justify-center">
              {/* Glow Effect */}
              <div className={`absolute inset-0 blur-3xl bg-gradient-to-r from-[#ff6b35]/20 via-teal-500/20 to-[#ff6b35]/20 rounded-full scale-150 transition-all duration-1000 ${loaded ? 'opacity-100 scale-150' : 'opacity-0 scale-100'}`}></div>
              
              {/* Main Brand Name */}
              <div className="relative z-10 text-center">
                <h2 
                  className={`text-8xl lg:text-9xl font-black tracking-tighter bg-gradient-to-r from-white via-gray-100 to-gray-300 bg-clip-text text-transparent transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0 blur-0' : 'opacity-0 translate-y-8 blur-sm'}`}
                  style={{ fontFamily: "'Inter', sans-serif", fontWeight: 900 }}
                >
                  Atlas
                </h2>
                <h2 
                  className={`text-8xl lg:text-9xl font-black tracking-tighter bg-gradient-to-r from-[#ff6b35] via-[#ff8c5a] to-[#ff6b35] bg-clip-text text-transparent -mt-4 transition-all duration-700 delay-300 ${loaded ? 'opacity-100 translate-y-0 blur-0' : 'opacity-0 translate-y-8 blur-sm'}`}
                  style={{ fontFamily: "'Inter', sans-serif", fontWeight: 900 }}
                >
                  IQ
                </h2>
                
                {/* Tagline */}
                <p className={`mt-6 text-gray-400 text-lg tracking-widest uppercase font-medium transition-all duration-700 delay-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  Trade-Aware Intelligence
                </p>
                
                {/* Decorative Elements */}
                <div className={`flex items-center justify-center gap-3 mt-4 transition-all duration-700 delay-700 ${loaded ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}>
                  <div className="w-12 h-px bg-gradient-to-r from-transparent to-[#ff6b35]"></div>
                  <div className="w-2 h-2 rounded-full bg-[#ff6b35] animate-pulse"></div>
                  <div className="w-12 h-px bg-gradient-to-l from-transparent to-[#ff6b35]"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className={`absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 transition-all duration-700 delay-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <div className="w-px h-16 bg-gradient-to-b from-white/50 to-transparent"></div>
        <div className="mt-2 animate-bounce">
          <i className="ri-arrow-down-line text-white text-2xl"></i>
        </div>
      </div>
    </div>
  );
}
